import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays } from "lucide-react";

const STATUS_STYLES = {
  received: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  picking: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  packing: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  packed: "bg-signal-green/15 text-signal-green border-signal-green/30",
  loading: "bg-signal-orange/15 text-signal-orange border-signal-orange/30",
  dispatched: "bg-signal-green/15 text-signal-green border-signal-green/30",
  assigned: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  in_progress: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  paused: "bg-muted text-muted-foreground border-border",
  completed: "bg-signal-green/15 text-signal-green border-signal-green/30",
  waiting: "bg-signal-red/15 text-signal-red border-signal-red/30",
  unloading: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  unloaded: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  qc: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  qc_pending: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  qc_done: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  grn_done: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  putaway: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  awaiting_vehicle: "bg-muted text-muted-foreground border-border",
  vehicle_waiting: "bg-signal-red/15 text-signal-red border-signal-red/30",
  loaded: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  order_received: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  verification_pending: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  stock_check_pending: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  stock_available: "bg-signal-green/15 text-signal-green border-signal-green/30",
  stock_short: "bg-signal-red/15 text-signal-red border-signal-red/30",
  stock_reserved: "bg-signal-green/15 text-signal-green border-signal-green/30",
  picking_pending: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  picking_completed: "bg-signal-green/15 text-signal-green border-signal-green/30",
  qc_approved: "bg-signal-green/15 text-signal-green border-signal-green/30",
  packing_pending: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  packing_in_progress: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  packing_completed: "bg-signal-green/15 text-signal-green border-signal-green/30",
  documentation_pending: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  ready_for_dispatch: "bg-signal-green/15 text-signal-green border-signal-green/30",
  loading_pending: "bg-signal-orange/15 text-signal-orange border-signal-orange/30",
  loading_in_progress: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  loading_completed: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  lr_uploaded: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  feedback_pending: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  feedback_done: "bg-signal-green/15 text-signal-green border-signal-green/30",
  customer_issue: "bg-signal-red/15 text-signal-red border-signal-red/30",
  in_transit: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  delivered: "bg-signal-green/15 text-signal-green border-signal-green/30",
  pod_received: "bg-signal-green/15 text-signal-green border-signal-green/30",
  closed: "bg-signal-green/15 text-signal-green border-signal-green/30",
  blocked: "bg-signal-red/15 text-signal-red border-signal-red/30",
  cancelled: "bg-muted text-muted-foreground border-border",
  voided: "bg-signal-red/15 text-signal-red border-signal-red/30",
  created: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  draft: "bg-muted text-muted-foreground border-border",
  submitted: "bg-signal-blue/15 text-signal-blue border-signal-blue/30",
  partially_available: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  partially_dispatched: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
  fully_dispatched: "bg-signal-green/15 text-signal-green border-signal-green/30",
  picking_in_progress: "bg-signal-amber/15 text-signal-amber border-signal-amber/30",
};

export function StatusBadge({ status, testid }) {
  const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground border-border";
  return (
    <Badge variant="outline" className={`${cls} font-mono text-[10px] uppercase tracking-wider`} data-testid={testid || `status-badge-${status}`}>
      {(status || "").replace(/_/g, " ")}
    </Badge>
  );
}

export function DateField({ label, value, onChange, testId, required }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const [month, setMonth] = useState(selected || new Date());
  const setDate = (date) => {
    if (!date) return;
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    onChange(iso);
    setOpen(false);
  };
  return (
    <div>
      <Label className="text-xs">{label}{required ? " *" : ""}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid={testId}>
            <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
            {selected ? selected.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Pick date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[70]" align="start" side="bottom" collisionPadding={16} data-testid={`${testId}-calendar`}>
          <Calendar mode="single" selected={selected} onSelect={setDate} initialFocus
            month={month} onMonthChange={setMonth}
            disabled={{ before: new Date(month.getFullYear(), month.getMonth(), 1), after: new Date(month.getFullYear(), month.getMonth() + 1, 0) }} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const TONES = {
  default: "text-foreground",
  red: "text-signal-red",
  amber: "text-signal-amber",
  green: "text-signal-green",
  blue: "text-signal-blue",
  orange: "text-signal-orange",
};

export function StatCard({ label, value, sub, icon: Icon, tone = "default", testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:bg-accent/40 transition-colors duration-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <p className={`metric text-2xl sm:text-3xl font-extrabold mt-1 ${TONES[tone]}`}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-xl sm:text-2xl font-bold tracking-tight uppercase">{children}</h2>
      {right}
    </div>
  );
}
